/**
 * The canvas's realtime subscription (`specs/M10-D1-dashboards.md` §6.2) — the one hook that
 * closes **P-182** (`source.refresh` had no caller) and **P-183** (no second tab ever repainted)
 * together, because both were the same missing subscription one layer apart.
 *
 * It joins the **board** rooms the canvas aggregates rather than a `dashboard:` room, and that is
 * not a shortcut: `services/widgets.ts` stamps every `widget.*` event with the parent's board id
 * as well as the dashboard id (`resolveParent`), so a board room already carries both halves —
 * the widget events that patch the grid and the `cell.changed` / `item.created` / `item.deleted`
 * events §6.2 names as the three that re-aggregate. The alternative would be a new
 * `dashboard:join` socket message with its own permission gate, for rooms whose events the
 * viewer can already receive.
 *
 * The data events go through `source.refresh(stamp)`, which is the 5 s trailing debounce
 * (`hooks.ts`): a burst of cell writes costs one re-aggregation, and the stamp is the event's own
 * id, so two tabs watching the same change ask the server the same question once.
 */
import { useEffect, useRef } from 'react';
import type { EventEnvelope } from '@elizmonday/shared';
import { joinBoard, leaveBoard, onEvent } from '../../lib/realtime/socket';
import type { CanvasParent } from './types';

/** The grid changed: re-read `dashboards.widgets`. */
const WIDGET_EVENTS = new Set(['widget.created', 'widget.updated', 'widget.moved', 'widget.deleted']);
/** §6.2's three: the numbers changed, so the aggregations behind them are stale. */
const DATA_EVENTS = new Set(['cell.changed', 'item.created', 'item.deleted']);

/** Does this `widget.*` envelope belong to the canvas we are mounted on? */
export function widgetEventMatches(event: EventEnvelope, parent: CanvasParent): boolean {
  if (parent.kind === 'dashboard') return event.dashboardId !== null && String(event.dashboardId) === parent.id;
  const payloadParent = (event.payload as { parent?: { kind?: string; id?: string } } | undefined)?.parent;
  return payloadParent?.kind === 'board_view' && String(payloadParent.id) === parent.id;
}

export interface DashboardRealtimeTarget {
  parent: CanvasParent;
  boardIds: readonly string[];
  /** The grid itself changed — re-read the widget list. */
  onWidgets(): void;
  /** A connected board's data changed — §6.2's debounced re-aggregation. */
  onData(stamp: string): void;
}

export function useDashboardRealtime(target: DashboardRealtimeTarget): void {
  // The target closes over queries and is a new object every render; read it through a ref so
  // the effect's identity is the room list alone (the same shape `useBoardRealtime` uses).
  const ref = useRef(target);
  ref.current = target;
  const rooms = [...target.boardIds].sort().join(',');

  useEffect(() => {
    const boardIds = rooms === '' ? [] : rooms.split(',');
    if (boardIds.length === 0) return undefined;
    let cancelled = false;

    for (const boardId of boardIds) {
      // A refusal is not an error here: a canvas may connect a board this viewer cannot read,
      // and the rest of the rooms still make it live.
      void joinBoard(boardId).catch(() => null);
    }

    const off = onEvent((event) => {
      if (cancelled) return;
      const owner = ref.current;
      if (WIDGET_EVENTS.has(event.type)) {
        if (widgetEventMatches(event, owner.parent)) owner.onWidgets();
        return;
      }
      if (!DATA_EVENTS.has(event.type)) return;
      if (event.boardId === null || !boardIds.includes(String(event.boardId))) return;
      owner.onData(String(event.id));
    });

    return () => {
      cancelled = true;
      off();
      for (const boardId of boardIds) leaveBoard(boardId);
    };
  }, [rooms]);
}
